/**
 * Thin typed fetch wrapper used by the API hooks in src/api/*.ts.
 *
 * Two backend domains exist in the HY platform:
 *   - `sales`     — hooten-young-sales (handles /api/sales/* + /api/depletions/* + /api/auth/*)
 *   - `marketing` — hooten-young-marketing (handles /api/marketing/*, not yet deployed)
 *
 * Each call specifies which domain it targets; the client resolves the
 * base URL from VITE_SALES_API_URL or VITE_MARKETING_API_URL.
 *
 * In local dev you have two options:
 *   (a) Set VITE_SALES_API_URL=http://localhost:8000 in .env.local —
 *       calls go cross-origin directly to the FastAPI backend (CORS is
 *       enabled for localhost in the backend).
 *   (b) Leave VITE_SALES_API_URL empty — the Vite dev proxy in
 *       vite.config.ts routes /api/* to localhost:8000 same-origin.
 *
 * Auth
 * ----
 * Every request automatically attaches `Authorization: Bearer <token>`
 * if a token is in `localStorage` under `AUTH_TOKEN_KEY`. On 401, the
 * client emits a `hy:auth-expired` window event and clears the token —
 * the AuthProvider listens for that event and redirects to /login.
 */

import { getActAsRepId, isRepSurfacePath } from './actAs';

const SALES_BASE = (import.meta.env.VITE_SALES_API_URL ?? '').replace(/\/$/, '');
const MARKETING_BASE = (import.meta.env.VITE_MARKETING_API_URL ?? '').replace(/\/$/, '');

const BASES = {
  sales: SALES_BASE,
  marketing: MARKETING_BASE,
} as const;

export type ApiDomain = keyof typeof BASES;

/** localStorage key for the persisted JWT access token. */
export const AUTH_TOKEN_KEY = 'hy.auth.token';

/** Custom window event fired when the server reports an expired/invalid token. */
export const AUTH_EXPIRED_EVENT = 'hy:auth-expired';

/**
 * Structured error from the auth API: `{detail: {code, message}}` form.
 * Generic 4xx may instead use FastAPI's `{detail: string}` form, in
 * which case the consumer should fall back to the message itself.
 */
export interface ApiErrorBody {
  detail?:
    | {
        code?: string;
        message?: string;
      }
    | string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: ApiErrorBody,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Convenience: pull the structured code from the body, if any. */
  get code(): string | undefined {
    if (this.body?.detail && typeof this.body.detail === 'object') {
      return this.body.detail.code;
    }
    return undefined;
  }

  /** Convenience: pull the human message, falling back to `.message`. */
  get serverMessage(): string {
    const d = this.body?.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object' && d.message) return d.message;
    return this.message;
  }
}

export type QueryParams = Record<string, string | number | undefined | null>;

function buildUrl(domain: ApiDomain, path: string, params?: QueryParams): string {
  const base = BASES[domain];
  const url = `${base}${path}`;
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function parseErrorBody(response: Response): Promise<ApiErrorBody | undefined> {
  try {
    const text = await response.text();
    if (!text) return undefined;
    try {
      return JSON.parse(text) as ApiErrorBody;
    } catch {
      // Not JSON; return as a string detail.
      return { detail: text.slice(0, 500) };
    }
  } catch {
    return undefined;
  }
}

/**
 * Internal: wraps fetch with token injection + uniform error handling.
 * `endpoint` is the auth-protected nature: when 401 fires we emit the
 * expired event so the SPA can redirect to /login.
 */
async function request<T>(
  domain: ApiDomain,
  method: string,
  path: string,
  params?: QueryParams,
  jsonBody?: unknown,
): Promise<T> {
  const url = buildUrl(domain, path, params);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...authHeader(),
  };
  // A FormData body (file upload) sets its own multipart boundary.
  const isForm = jsonBody instanceof FormData;
  if (jsonBody !== undefined && !isForm) {
    headers['Content-Type'] = 'application/json';
  }
  // Admin "act as rep": scope the rep workspace to the selected rep.
  //
  // /api/notes/* is included because Team Chat is a tab in that
  // workspace — without it an admin viewing as a rep would see three
  // tabs of the rep's data and one of their own, including a wrong
  // unread badge.
  //
  // The backend ignores/blocks the header unless the authenticated
  // caller is an admin, so sending it is never an escalation.
  //
  // Gated on the SURFACE, not just the endpoint. ActingAsBanner renders
  // only on rep-surface paths, so impersonating anywhere else means
  // acting as somebody with no visible indication that you are — and
  // that is exactly what happened: the home page's momentum panel calls
  // /api/field/momentum, picked up a stale act-as from sessionStorage,
  // and showed an admin a colleague's figures under their own heading.
  //
  // The same rule covers /api/notes/*: the Team Chat TAB inside the rep
  // workspace should show the rep's messages, while the standalone
  // /notes page is the admin's own mail.
  //
  // Writes matter more than reads here. A visit note or flag posted
  // from an unbannered surface would be attributed to the rep with
  // nothing on screen saying so.
  const impersonatablePath =
    (path.startsWith('/api/field/') || path.startsWith('/api/notes/')) &&
    isRepSurfacePath(window.location.pathname);
  if (impersonatablePath) {
    const actAs = getActAsRepId();
    if (actAs) headers['X-Act-As-Rep'] = actAs;
  }
  const response = await fetch(url, {
    method,
    headers,
    body: isForm ? jsonBody : jsonBody !== undefined ? JSON.stringify(jsonBody) : undefined,
  });

  if (!response.ok) {
    const body = await parseErrorBody(response);

    if (response.status === 401) {
      // Token is bad / expired / missing — kick the SPA out.
      localStorage.removeItem(AUTH_TOKEN_KEY);
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }

    const summary =
      typeof body?.detail === 'string'
        ? body.detail
        : (body?.detail?.message ?? `${response.status} ${response.statusText}`);
    throw new ApiError(response.status, summary, body);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function apiGet<T>(domain: ApiDomain, path: string, params?: QueryParams): Promise<T> {
  return request<T>(domain, 'GET', path, params);
}

export function apiPost<T>(
  domain: ApiDomain,
  path: string,
  body?: unknown,
  params?: QueryParams,
): Promise<T> {
  return request<T>(domain, 'POST', path, params, body ?? {});
}

/** POST a multipart form — e.g. a file upload. */
export function apiPostForm<T>(domain: ApiDomain, path: string, form: FormData): Promise<T> {
  return request<T>(domain, 'POST', path, undefined, form);
}

export function apiPut<T>(
  domain: ApiDomain,
  path: string,
  body?: unknown,
  params?: QueryParams,
): Promise<T> {
  return request<T>(domain, 'PUT', path, params, body ?? {});
}

export function apiPatch<T>(
  domain: ApiDomain,
  path: string,
  body?: unknown,
  params?: QueryParams,
): Promise<T> {
  return request<T>(domain, 'PATCH', path, params, body ?? {});
}

export function apiDelete<T>(domain: ApiDomain, path: string, params?: QueryParams): Promise<T> {
  return request<T>(domain, 'DELETE', path, params);
}

/**
 * POST and read a server-sent-events response, calling `onEvent` with
 * each `data:` payload as it arrives. Errors before the stream opens
 * (4xx/5xx) throw ApiError exactly like `apiPost`, including the 401
 * sign-out behaviour.
 */
export async function apiPostStream(
  domain: ApiDomain,
  path: string,
  body: unknown,
  onEvent: (event: unknown) => void,
): Promise<void> {
  const response = await fetch(buildUrl(domain, path), {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok || !response.body) {
    const errBody = await parseErrorBody(response);
    if (response.status === 401) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }
    const summary =
      typeof errBody?.detail === 'string'
        ? errBody.detail
        : (errBody?.detail?.message ?? `${response.status} ${response.statusText}`);
    throw new ApiError(response.status, summary, errBody);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const data = chunk
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trimStart())
        .join('\n');
      if (data) onEvent(JSON.parse(data));
      boundary = buffer.indexOf('\n\n');
    }
  }
}
